import { useContext, useEffect, useState } from "react";
import styles from "./VolumeContentForm.module.css";
import {
    ActionList,
    Button,
    Icon,
    IconSource,
    InlineStack,
    Popover,
    Text,
    TextField,
    Thumbnail,
    Tooltip,
} from "@shopify/polaris";
import { CollectionIcon, SearchIcon, DeleteIcon } from "@shopify/polaris-icons";
import { ProductObj, VariantsObj } from "../../Types";
import CloseIcon from "../../../../components/Icons/CloseIcon";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { getProductsDynamicallyAndReturnNewProductsArray } from "../../constants";
import { getDiscountOptionsWIthoutFixed } from "../../constants";
import { CurrencyTypes } from "../../../constants";
import useIsMobile from "../../../../hooks/useIsMobile";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { FormSelect, FormTextField, FormCheckbox } from "../../../../components/FormFields";

type UpsellVolumeProps = {
    handleInputChange: (index: number, field: any, value: string | number | boolean | ProductObj[]) => void;
    upsell_products: any[];
    allow_upsell_products: boolean;
    selectedTab: number;
    submit: () => void;
    excludeProducts: ProductObj[];
    currency: string;
    offerError?: string;
};

const getCurrencySymbol = (currencyCode: string) => {
    const currency = CurrencyTypes.find((c) => c.country_code === currencyCode);
    return currency ? currency.symbol : "";
};

function getDiscountSuffix(discountType: string, currency: string) {
    if (discountType === "percentage") {
        return "%";
    }
    if (discountType === "fixed" || discountType === "flat") {
        return getCurrencySymbol(currency);
    }
    return "";
}

const UpsellVolume = ({
    handleInputChange,
    allow_upsell_products,
    upsell_products,
    selectedTab,
    submit,
    excludeProducts,
    currency,
    offerError,
}: UpsellVolumeProps) => {
    const [productSearchQuery, setProductSearchQuery] = useState("");
    const { isMobile } = useIsMobile();
    const [activePopoverProductId, setActivePopoverProductId] = useState<string | null>(null);
    const togglePopover = (productId: string) => {
        setActivePopoverProductId((prev) => (prev === productId ? null : productId));
    };

    const handleVariableInsert = (productId: string, variable: string) => {
        const product = upsell_products.find((p) => p.id === productId);

        if (!product) return;

        handleProductPropertyChange(productId, "upsell_text", `${product.upsell_text || ""} ${variable}`);

        setActivePopoverProductId(null);
    };

    const context = useContext(AppConfigContext);
    const { sft } = context;

    const handleProductSearch = async () => {
        const preSelected = upsell_products?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants?.map((variant: VariantsObj) => ({
                id: variant?.id,
            })),
        }));

        const selected = await (window.shopify || shopify).resourcePicker({
            type: "product",
            multiple: true,
            query: productSearchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.forEach((product: any) => {
                if (!excludeProducts.find((ep) => ep.id === product?.id)) {
                    productsArr.push({
                        id: product?.id,
                        images: product?.images,
                        title: product?.title,
                        handle: product?.handle,
                        quantity: 1,
                        maxProductStock: product?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        checked: false,
                        visible_when_bar_selected: false,
                        isSubscribed: false,
                        variants: product?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,
                            title: variant?.title?.toLowerCase() === "default title" ? product?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: product?.options,
                    });
                } else {
                    (window.shopify || shopify).toast.show("Included product cannot be added as upsell(s)", {
                        isError: true,
                    });
                }
            });

            setProductSearchQuery("");

            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(productsArr, sft);
                const mappedProducts = newProductsArr.map((p: any) => {
                    const existing = upsell_products.find((ep: any) => ep.id === p.id);
                    return {
                        ...p,
                        discountType: existing?.discountType || "percentage",
                        discountValue: existing?.discountValue ?? 0,
                        quantity: existing?.quantity ?? 1,
                        upsell_text: existing?.upsell_text ?? "{product_title}",
                        checked: existing?.checked ?? false,
                        visible_when_bar_selected: existing?.visible_when_bar_selected ?? false,
                    };
                });
                handleInputChange(selectedTab, "upsell_products", mappedProducts);
            } catch (error) {
                console.error(error);
                const mappedProducts = productsArr.map((p: any) => {
                    const existing = upsell_products.find((ep: any) => ep.id === p.id);
                    return {
                        ...p,
                        discountType: existing?.discountType || "percentage",
                        discountValue: existing?.discountValue ?? 0,
                    };
                });
                handleInputChange(selectedTab, "upsell_products", mappedProducts);
            }
        }
    };

    const removeProduct = (pId: string = "") => {
        const temp = [...upsell_products];
        const idx = temp.findIndex((p: any) => p.id === pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            handleInputChange(selectedTab, "upsell_products", temp);
        }
    };

    const handleProductPropertyChange = (pId: string, field: string, value: any) => {
        const temp = upsell_products.map((p: any) => {
            if (p.id === pId) {
                return { ...p, [field]: value };
            }
            return p;
        });
        handleInputChange(selectedTab, "upsell_products", temp);
    };

    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
            return;
        }

        if (upsell_products?.length) {
            submit();
        }

        if (!allow_upsell_products) {
            submit();
        }

        return () => {
            if (upsell_products?.length && allow_upsell_products) {
                submit();
            }
        };
    }, [upsell_products, allow_upsell_products]);

    return (
        <div
            className={styles.gift_container}
            style={{
                position: "relative",
                marginTop: "15px",
            }}
        >
            <span
                style={{
                    position: "absolute",
                    right: "5px",
                    top: "5px",
                    cursor: "pointer",
                }}
                onClick={() => handleInputChange(selectedTab, "allow_upsell_products", false)}
            >
                <Icon source={DeleteIcon as IconSource} tone="critical" accessibilityLabel="Delete" />
            </span>

            <h5 style={{ fontWeight: "600", marginBottom: "8px" }}>Upsell Products</h5>
            {/* <TextField
                label="Upsell Text"
                placeholder="e.g. + Add at {discount} discount"
                autoComplete="off"
                value={upsell_text || ""}
                onChange={(val) => handleInputChange(selectedTab, "upsell_text", val)}
                helpText="Text displayed on the storefront for each upsell product. Use {discount} as placeholder."
            /> */}
            <TextField
                label={"Search Product"}
                labelHidden
                placeholder="Search Products"
                autoComplete={"off"}
                value={productSearchQuery}
                onChange={(val) => {
                    setProductSearchQuery(val);
                }}
                prefix={<Icon source={SearchIcon} />}
                size="slim"
                connectedRight={
                    <div style={{ marginLeft: "8px" }}>
                        <Button onClick={() => handleProductSearch()} size="slim">
                            Browse
                        </Button>
                    </div>
                }
                error={offerError}
            />

            {upsell_products?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                    {upsell_products?.map((p: ProductObj, pIndex: number) => (
                        <div
                            key={p.id}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                border: "1px solid rgba(227, 227, 227, 1)",
                                borderRadius: "8px",
                                background: "#ffffff",
                                padding: "12px",
                                gap: "10px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                                    <Thumbnail
                                        source={p?.images?.[0]?.originalSrc || CollectionIcon}
                                        alt={p?.title}
                                        size="small"
                                    />
                                    <span style={{ fontWeight: "500" }}>{p?.title}</span>
                                </div>

                                <div style={{ width: isMobile ? 100 : 110 }}>
                                    <FormTextField
                                        name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.quantity`}
                                        label=""
                                        labelHidden
                                        type="number"
                                        min={1}
                                        prefix="Qty"
                                        autoComplete="off"
                                        value={(p.quantity ?? 1).toString()}
                                        onChange={(val) => {
                                            const qty = Math.max(1, parseInt(val || "1", 10));
                                            handleProductPropertyChange(p.id, "quantity", qty);
                                        }}
                                    />
                                </div>

                                <div onClick={() => removeProduct(p.id)} style={{ cursor: "pointer", display: "flex" }}>
                                    <CloseIcon />
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "10px", flexDirection: isMobile ? "column" : "row" }}>
                                <div style={{ flex: 1 }}>
                                    <FormSelect
                                        name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.discountType`}
                                        label="Discount Type"
                                        options={getDiscountOptionsWIthoutFixed(getCurrencySymbol(currency))}
                                        value={p.discountType || "percentage"}
                                        onChange={(val) => handleProductPropertyChange(p.id, "discountType", val)}
                                    />
                                </div>
                                <div style={{ flex: 1, visibility: p?.discountType === "none" ? "hidden" : "visible" }}>
                                    <FormTextField
                                        name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.discountValue`}
                                        label="Discount Value"
                                        type="number"
                                        value={(p.discountValue ?? 0).toString()}
                                        suffix={getDiscountSuffix(p.discountType || "percentage", currency)}
                                        onChange={(val) => {
                                            const regex = /^(\d+(\.\d{0,2})?)?$/;
                                            if (regex.test(val)) {
                                                const type = p.discountType || "percentage";
                                                let numericVal = parseFloat(val) || 0;
                                                if (type === "percentage" && numericVal > 100) {
                                                    numericVal = 100;
                                                }
                                                handleProductPropertyChange(p.id, "discountValue", numericVal);
                                            }
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <div style={{ flex: 1 }}>
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text as="span" variant="bodyMd" fontWeight="regular">
                                            Upsell Text
                                        </Text>
                                        <Popover
                                            active={activePopoverProductId === p.id}
                                            activator={
                                                <div
                                                    onClick={() => togglePopover(p.id)}
                                                    style={{ cursor: "pointer", display: "flex" }}
                                                >
                                                    <Tooltip content="Add variable">
                                                        <PiBracketsCurlyBold />
                                                    </Tooltip>
                                                </div>
                                            }
                                            onClose={() => setActivePopoverProductId(null)}
                                        >
                                            <ActionList
                                                items={[
                                                    {
                                                        content: "Product title",
                                                        onAction: () => handleVariableInsert(p.id, "{product_title}"),
                                                    },
                                                    {
                                                        content: "Discount",
                                                        onAction: () => handleVariableInsert(p.id, "{discount}"),
                                                    },
                                                ]}
                                            />
                                        </Popover>
                                    </InlineStack>

                                    <div style={{ marginTop: "4px" }}>
                                        <FormTextField
                                            name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.upsell_text`}
                                            label=""
                                            labelHidden
                                            placeholder="Get this at {discount} discount"
                                            autoComplete="off"
                                            value={p.upsell_text || ""}
                                            onChange={(val) => handleProductPropertyChange(p.id, "upsell_text", val)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <div style={{ flex: 1 }}>
                                    <FormCheckbox
                                        name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.checked`}
                                        label={"Mark this upsell as checked by default"}
                                        checked={p?.checked || false}
                                        onChange={(val) => {
                                            handleProductPropertyChange(p.id, "checked", val);
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <div style={{ flex: 1 }}>
                                    <FormCheckbox
                                        name={`volume_discounts.${selectedTab}.upsell_products.${pIndex}.visible_when_bar_selected`}
                                        label={"Visible only when bar is selected."}
                                        checked={p?.visible_when_bar_selected || false}
                                        onChange={(val) => {
                                            handleProductPropertyChange(p.id, "visible_when_bar_selected", val);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UpsellVolume;
